/*
===============================================================================
bieloadsession.models.ts

Client models for:
    middleware service: bieloadsession

Middleware methods:
    find
    get
    add
    edit
    combo
    cancel
    stage
    reopen
===============================================================================
*/

import { BieRecordId } from "./bieonboardingworkbench.models";

export type BieLoadSessionMode = 'ADD' | 'EDIT' | 'VIEW';

export type BieLoadSessionStateCode =
  | 'DRAFT'
  | 'STAGED'
  | 'VALIDATED'
  | 'PUBLISHED'
  | 'REJECTED'
  | 'CANCELLED'
  | 'ROLLEDBACK'
  | string;

export type BieLoadScopeCode =
  | 'INSTITUTION_SETUP'
  | 'HISTORICAL_BASELINE'
  | 'HISTORICAL_CORRECTION'
  | 'MIGRATION'
  | string;

export type BieWorkbookStatus =
  | 'NOT_UPLOADED'
  | 'EMPTY'
  | 'STAGED'
  | 'STAGED_WITH_WARNINGS'
  | 'STAGED_WITH_ERRORS'
  | string;

export interface BieSaveResult {
  rid: number;
  stp: string;
}

export interface BieLoadSessionFindRequest {
  search?: string;

  stateCode?: string;

  scopeCode?: string;

  sourceSystem?: string;

  coverageStartDate?: string;

  coverageEndDate?: string;

  status?: number | null;

  pageOffset?: number;

  pageLimit?: number;
}

export interface BieLoadSessionAddPayload {
  scopeId: number;

  name: string;

  shortcode: string;

  description?: string | null;

  sourceOrganization: string;

  sourceSystem: string;

  coverageStartDate: string;

  coverageEndDate: string;

  historicalCutoffDate: string;

  extraInfo?: Record<string, any>;
}

export interface BieLoadSessionEditPayload extends BieLoadSessionAddPayload {
  loadSessionId: number;

  stamp: string;
}

export interface BieLoadSessionCancelPayload {
  loadSessionId: number;

  remarks: string;

  stamp: string;
}

export interface BieLoadSessionTransitionPayload {
  loadSessionId: number;
  stamp: string;
}

export interface BieLoadSessionComboRequest {
  search?: string;

  stateCode?: string;

  scopeCode?: string;

  limit?: number;
}

export interface BieLoadSessionComboItem {
  rid: number;

  rix: string;

  shortcode: string;

  name: string;

  stateCode: string;

  stateName: string;

  scopeId: number;

  scopeCode: string;

  scopeName: string;

  sourceSystem: string;

  coverageStartDate: string | null;

  coverageEndDate: string | null;

  stamp: string;

  raw: any;
}


export interface BieLoadSessionScopeComboRequest {
  search?: string;

  limit?: number;
}



export interface BieLoadSessionScopeComboItem {
  rid: number;

  rix: string;

  shc: string;

  nam: string;

  dsc: string;

  stp: string;

  raw: any;
}

export interface BieLoadSessionStatistics {
  importCount: number;

  historicalFactCount: number;

  forecastBatchCount: number;

  onboardingImportCount: number;

  onboardingItemCount: number;

  pendingCount: number;

  validCount: number;

  warningCount: number;

  failedCount: number;

  resolvedRecordClassCount: number;

  unresolvedRecordClassCount: number;

  issueCount: number;

  informationIssueCount: number;

  warningIssueCount: number;

  errorIssueCount: number;

  blockingIssueCount: number;

  unresolvedReferenceCount: number;

  validationStatus: string;

  validationReady: boolean;

  importClassSummary: any;
}

export interface BieLoadSessionWorkbook {
  available: boolean;

  status: BieWorkbookStatus;

  uploadRequired: boolean;

  replaceAllowed: boolean;

  latestImportId: number | null;

  latestImportIx: string | null;

  latestImportTrackId: string | null;

  sourceId: number | null;

  sourceCode: string | null;

  sourceName: string | null;

  latestImportClassId: number | null;

  latestImportClassCode: string | null;

  latestImportClassName: string | null;

  latestImportStateId: number | null;

  latestImportStateCode: string | null;

  latestImportStateName: string | null;

  latestSheetName: string | null;

  filename: string | null;

  version: string | null;

  checksum: string | null;

  uploaderId: number | null;

  uploaderIx: string | null;

  uploaderCode: string | null;

  uploaderName: string | null;

  uploaderEmail: string | null;

  uploadedAt: string | null;

  createdAt: string | null;

  latestImportStamp: string | null;
}

export interface BieLoadSessionActions {
  canEditContext: boolean;

  canUploadWorkbook: boolean;

  canReplaceWorkbook: boolean;

  canOpenWorkbench: boolean;

  canValidate: boolean;

  canCancel: boolean;

  canReopen: boolean;

  readOnly: boolean;
}

export interface BieLoadSession {
  rid: number;

  rix: string;

  name: string;

  shortcode: string;

  description: string | null;

  scopeId: number;

  scopeCode: BieLoadScopeCode;

  scopeName: string;

  stateId: number;

  stateCode: BieLoadSessionStateCode;

  stateName: string;

  sourceOrganization: string;

  sourceSystem: string;

  coverageStartDate: string | null;

  coverageEndDate: string | null;

  historicalCutoffDate: string | null;

  observedCoverageStartDate: string | null;

  observedCoverageEndDate: string | null;

  historicalCoverageStatus: string;

  historicalCoverageLabel: string;

  creatorId: number | null;

  validatorId: number | null;

  validatedAt: string | null;

  publisherId: number | null;

  publishedAt: string | null;

  cancellerId: number | null;

  cancelledAt: string | null;

  created: string | null;

  extraInfo: Record<string, any>;

  status: number;

  stamp: string;

  statistics: BieLoadSessionStatistics;

  workbook: BieLoadSessionWorkbook;

  actions: BieLoadSessionActions;

  totalCount?: number;

  raw: any;
}

export interface BieLoadSessionFindResult {
  rows: BieLoadSession[];

  totalCount: number;

  pageOffset: number;

  pageLimit: number;
}

export interface IBieLoadSessionStageResult {
  rid: BieRecordId;
  state: BieLoadSessionStateCode;
  importCount: number;
  stamp: string;
}

export interface IBieLoadSessionReopenResult {
  rid: BieRecordId;
  state: BieLoadSessionStateCode;
  stamp: string;
}

export const defaultBieLoadSessionFindRequest: Required<
  Omit<BieLoadSessionFindRequest, 'status'>
> & {
  status: number | null;
} = {
  search: '',
  stateCode: '',
  scopeCode: '',
  sourceSystem: '',
  coverageStartDate: '',
  coverageEndDate: '',
  status: 1,
  pageOffset: 0,
  pageLimit: 50,
};

export const emptyBieLoadSessionAddPayload: BieLoadSessionAddPayload = {
  scopeId: 0,
  name: '',
  shortcode: '',
  description: '',
  sourceOrganization: '',
  sourceSystem: '',
  coverageStartDate: '',
  coverageEndDate: '',
  historicalCutoffDate: '',
  extraInfo: {},
};
